// Lógica pura de la conexión de Mercado Libre en el front: qué aviso llegó del popup
// y qué decirle a la persona según el código de error. Sin red ni DOM, para
// probarla con vitest. Los efectos viven en `mercadoLibreConexion.ts`.
//
// Todo Mercado Libre vive en atlas-backend (ADR-2026-09-28-mercadolibre-vive-en-el-backend):
// el front nunca habla con Mercado Libre ni lee las tablas `ml_*`. Este módulo no
// comparte código con Instagram ni con Tiendanube a propósito: son integraciones
// separadas y un cambio en una no tiene que mover a la otra.

/**
 * Mientras la integración se prueba, sólo la ven los dev testers (botón de Mi marca y
 * chat). Cuando esté lista para todos, esto pasa a `true`. Además el backend responde
 * 404 en todas las rutas con `ML_ENABLED=false`: con el interruptor apagado del otro
 * lado, el botón no se dibuja aunque esto esté en `true`.
 */
export const MERCADOLIBRE_PARA_TODOS = false;

/** ¿Esta persona ve la conexión de Mercado Libre? */
export function veMercadoLibre(isDevTester: boolean | null | undefined): boolean {
  return MERCADOLIBRE_PARA_TODOS || isDevTester === true;
}

/** Se dispara en `window` al conectar o desconectar, para que el chat se entere en el momento. */
export const EVENTO_CAMBIO_DE_MERCADOLIBRE = "atlas:mercadolibre-cambio";

/** Clave donde `ml-conectado.html` deja el aviso por si se perdió el `opener`. */
export const CLAVE_AVISO_ML = "atlas:ml-conectado";

/** El `type` que manda `ml-conectado.html`. */
export const TIPO_AVISO_ML = "ml-conectado";

/** Un aviso guardado hace más que esto es de un intento viejo: se ignora. */
export const VIDA_DEL_AVISO_ML_MS = 15 * 60 * 1000;

/** Lo que tarda como máximo alguien en autorizar (el state del backend vence a los 10 min). */
export const ESPERA_MAXIMA_ML_MS = 10 * 60 * 1000;

export type AvisoDeMercadoLibre = { reclamo: string } | { error: string };

/**
 * Lee lo que mandó `ml-conectado.html` (por postMessage o por localStorage).
 * Devuelve null si no es un aviso nuestro, si está vencido o si el reclamo no
 * tiene la forma esperada (el backend lo genera con 32 bytes en base64url).
 */
export function leerAvisoDeMercadoLibre(data: unknown, ahora = Date.now()): AvisoDeMercadoLibre | null {
  let d = data;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return null;
    }
  }
  if (!d || typeof d !== "object") return null;
  const a = d as { type?: unknown; reclamo?: unknown; error?: unknown; t?: unknown };
  if (a.type !== TIPO_AVISO_ML) return null;
  if (typeof a.t === "number" && ahora - a.t > VIDA_DEL_AVISO_ML_MS) return null;
  if (typeof a.error === "string" && /^[a-z_]{1,40}$/.test(a.error)) return { error: a.error };
  if (typeof a.reclamo === "string" && /^[A-Za-z0-9_-]{32,200}$/.test(a.reclamo)) return { reclamo: a.reclamo };
  return null;
}

/** Qué decirle a la persona según el código que devolvió el backend o el popup. */
export function mensajeDeErrorDeMercadoLibre(code: string): string {
  switch (code) {
    // ── Vienen del popup (el backend los traduce de Mercado Libre) ──
    case "cancelado":
      return "Cancelaste la conexión en Mercado Libre. Cuando quieras, probá de nuevo.";
    case "operador":
      return "Entraste a Mercado Libre con una cuenta de colaborador. Sólo el dueño de la cuenta puede conectarla: pedile que entre él o usá su usuario.";
    case "otro_pais":
      return "Por ahora Atlas se conecta sólo con cuentas de Mercado Libre Argentina.";
    case "sin_permisos":
      return "Mercado Libre no nos dio todos los permisos que hacen falta. Probá de nuevo y aceptá todo lo que te pide.";
    case "datos_pendientes":
      return "Tu cuenta de Mercado Libre tiene datos pendientes de completar. Entrá a Mercado Libre, completalos y probá de nuevo.";
    case "vencido":
    case "reclamo_invalido":
    case "tiempo":
      return "La conexión tardó demasiado y se venció. Probá de nuevo.";
    // ── Vienen del backend ──
    case "cuenta_en_otra_marca":
      return "Esa cuenta de Mercado Libre ya está conectada a otra marca. Desconectala ahí primero.";
    case "marca_con_otra_cuenta":
      return "Esta marca ya tiene otra cuenta de Mercado Libre conectada. Desconectala primero.";
    case "reconectar":
      return "Mercado Libre pidió volver a conectar la cuenta. Tocá Reconectar.";
    case "marca_ajena":
    case "sin_marca":
      return "No encontramos la marca. Recargá la página y probá de nuevo.";
    case "no_disponible":
    case "no_configurado":
    case "origen_no_permitido":
      return "La conexión con Mercado Libre todavía no está disponible desde acá.";
    // ── Del navegador ──
    // Sin respuesta del backend (wifi que corta, red corporativa que inspecciona el tráfico): no es
    // que Mercado Libre haya fallado, así que no se dice eso (pasó el 29/9 con la red de la oficina).
    case "red":
      return "No pudimos comunicarnos con Atlas. Revisá tu conexión a internet y probá de nuevo.";
    case "sin_sesion":
      return "Tu sesión venció. Volvé a iniciar sesión y probá de nuevo.";
    case "popup_bloqueado":
      return "Tu navegador bloqueó la ventana de Mercado Libre. Permití las ventanas emergentes para Atlas y probá de nuevo.";
    case "ventana_cerrada":
      return "Se cerró la ventana de Mercado Libre antes de terminar. Probá de nuevo.";
    default:
      return "Mercado Libre no terminó de conectar la cuenta. Probá de nuevo en un rato.";
  }
}

/** El error que tira la conexión: `code` estable para decidir, `message` para mostrar. */
export class ErrorDeMercadoLibre extends Error {
  constructor(readonly code: string) {
    super(mensajeDeErrorDeMercadoLibre(code));
    this.name = "ErrorDeMercadoLibre";
  }
}

export interface EstadoDeMercadoLibre {
  /** El backend tiene la integración prendida (si no, el botón no se dibuja). */
  disponible: boolean;
  conectada: boolean;
  /** activa | reconectar; null si nunca se conectó. */
  estado: "activa" | "reconectar" | null;
  nickname: string | null;
  /** `true` = no pudimos preguntar: `conectada: false` quiere decir "no sabemos". */
  error: boolean;
}

/**
 * Traduce la respuesta de `GET /v1/mercadolibre/estado`. Un 404 es la integración
 * apagada en el backend (`ML_ENABLED=false`): no es un error, es "no está disponible".
 */
export function estadoDesdeRespuesta(status: number, cuerpo: unknown): EstadoDeMercadoLibre {
  const nada = { conectada: false, estado: null, nickname: null } as const;
  if (status === 404) return { disponible: false, ...nada, error: false };
  if (status < 200 || status >= 300 || !cuerpo || typeof cuerpo !== "object") {
    return { disponible: false, ...nada, error: true };
  }
  const d = cuerpo as { configurado?: unknown; conectada?: unknown; estado?: unknown; nickname?: unknown };
  const estado = d.estado === "activa" || d.estado === "reconectar" ? d.estado : null;
  return {
    // Prendida pero sin credenciales cargadas: tampoco se puede conectar.
    disponible: d.configurado !== false,
    conectada: d.conectada === true,
    estado,
    nickname: typeof d.nickname === "string" && d.nickname ? d.nickname : null,
    error: false,
  };
}

/** Saca el código de error estable de una respuesta del backend (`{ ok:false, error }`). */
export function codigoDeError(status: number, cuerpo: unknown): string {
  if (status === 404) return "no_disponible";
  if (status === 401) return "sin_sesion";
  const e = cuerpo && typeof cuerpo === "object" ? (cuerpo as { error?: unknown }).error : null;
  return typeof e === "string" && e ? e : "error";
}
